import React, { useState } from 'react';
import { Report } from '../types';

const Reports: React.FC = () => {
  // sample/seed reports (kept from existing file)
  const seedReports: Report[] = [
    { id: 1, name: 'Daily Sales Report', type: 'Sales', date: '2024-11-19', status: 'ready' },
    { id: 2, name: 'Weekly Inventory Report', type: 'Inventory', date: '2024-11-18', status: 'ready' },
    { id: 3, name: 'Monthly Revenue Summary', type: 'Financial', date: '2024-11-01', status: 'processing' },
    { id: 4, name: 'Store Performance Report', type: 'Performance', date: '2024-11-15', status: 'ready' },
    { id: 5, name: 'Product Analytics', type: 'Analytics', date: '2024-11-17', status: 'ready' }
  ];

  const [reports] = useState<Report[]>(seedReports);
  const [reportType, setReportType] = useState<string>('All');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [generated, setGenerated] = useState<Report[] | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);

  const availableTypes = ['All', 'Sales', 'Inventory', 'Financial', 'Performance', 'Analytics'];

  function withinRange(dateStr: string) {
    if (!startDate && !endDate) return true;
    const d = new Date(dateStr);
    if (startDate && d < new Date(startDate)) return false;
    if (endDate && d > new Date(endDate)) return false;
    return true;
  }

  const filteredReports = reports.filter(r => (reportType === 'All' || r.type === reportType) && withinRange(r.date));

  // Create CSV and trigger download
  function downloadCSVFromReports(list: Report[], filename = 'reports.csv') {
    if (!list || list.length === 0) {
      setMessage('No data to download');
      return;
    }
    const headers = Object.keys(list[0]).join(',');
    const rows = list.map(r => Object.values(r).map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    setDownloadUrl(url);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    // keep the object URL for possible re-download, revoke after short time
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  // Try to request backend to generate a report. If backend not available, fallback to local CSV build.
  async function generateReport() {
    setMessage(null);
    setGenerating(true);
    setGenerated(null);
    setDownloadUrl(null);

    const payload = {
      type: reportType === 'All' ? undefined : reportType,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      options: { format: 'csv' }
    };

    try {
      // attempt backend generation
      const res = await fetch('/api/reports', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        // backend unavailable or rejected — fallback to client-side CSV build using filteredReports
        setMessage('Backend generation failed or returned error — falling back to local export.');
        setGenerated(filteredReports);
        downloadCSVFromReports(filteredReports, `reports-fallback-${Date.now()}.csv`);
      } else {
        const data = await res.json();
        // Expecting either { downloadUrl } or { data: [...] } or { id, status }
        if (data.downloadUrl) {
          setMessage('Report ready — click Download.');
          setDownloadUrl(data.downloadUrl);
        } else if (Array.isArray(data.data)) {
          setGenerated(data.data);
          // offer immediate download
          downloadCSVFromReports(data.data, `reports-${Date.now()}.csv`);
          setMessage('Report generated locally from response.');
        } else if (data.id) {
          // accepted for processing — poll or show status link
          setMessage('Report queued for processing. Refresh or check Reports API for status.');
          // Optionally show a link to fetch by id: /api/reports/:id/download
        } else {
          // unknown response, fallback
          setMessage('Unexpected backend response — falling back to local export.');
          setGenerated(filteredReports);
          downloadCSVFromReports(filteredReports, `reports-fallback-${Date.now()}.csv`);
        }
      }
    } catch (err) {
      // network error -> fallback
      setMessage('Network error — using local export.');
      setGenerated(filteredReports);
      downloadCSVFromReports(filteredReports, `reports-offline-${Date.now()}.csv`);
    } finally {
      setGenerating(false);
    }
  }

  // helper to format date for display
  const fmt = (d: string) => new Date(d).toLocaleDateString();

  // summary counts (keep existing UI)
  const totalReports = reports.length;
  const readyReports = reports.filter(r => r.status === 'ready').length;
  const processingReports = reports.filter(r => r.status === 'processing').length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-xl font-bold text-gray-800">Reports</h3>
          <p className="text-gray-500">Generate and download business reports</p>
        </div>

        <div className="flex items-center gap-3">
          <select
            value={reportType}
            onChange={e => setReportType(e.target.value)}
            className="border rounded-lg px-3 py-2"
          >
            {availableTypes.map(t => <option key={t} value={t}>{t}</option>)}
          </select>

          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded-lg px-3 py-2" />
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded-lg px-3 py-2" />

          <button
            onClick={generateReport}
            className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition"
            disabled={generating}
          >
            {generating ? 'Generating…' : '+ Generate Report'}
          </button>
        </div>
      </div>

      {/* summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-black rounded-2xl p-6 shadow-lg">
          <div className="text-4xl mb-2">📊</div>
          <div className="text-3xl font-bold mb-1">{totalReports}</div>
          <div className="text-sm opacity-90">Total Reports</div>
        </div>
        <div className="bg-gradient-to-br from-green-500 to-green-600 text-black rounded-2xl p-6 shadow-lg">
          <div className="text-4xl mb-2">✅</div>
          <div className="text-3xl font-bold mb-1">{readyReports}</div>
          <div className="text-sm opacity-90">Ready to Download</div>
        </div>
        <div className="bg-gradient-to-br from-yellow-500 to-yellow-600 text-black rounded-2xl p-6 shadow-lg">
          <div className="text-4xl mb-2">⏳</div>
          <div className="text-3xl font-bold mb-1">{processingReports}</div>
          <div className="text-sm opacity-90">Processing</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-black rounded-2xl p-6 shadow-lg">
          <div className="text-4xl mb-2">📅</div>
          <div className="text-sm opacity-90">Scheduled</div>
        </div>
      </div>

      {/* available reports list */}
      <div className="bg-white rounded-2xl shadow-lg mb-6">
        <div className="p-6 border-b border-gray-200">
          <h4 className="font-bold text-gray-800">Available Reports</h4>
        </div>
        <div className="divide-y divide-gray-200">
          {filteredReports.map(report => (
            <div key={report.id} className="p-6 flex items-center justify-between hover:bg-gray-50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center text-2xl">📄</div>
                <div>
                  <div className="font-medium text-gray-900">{report.name}</div>
                  <div className="text-sm text-gray-500">{report.type} • Generated on {fmt(report.date)}</div>
                </div>
              </div>

              <div className="flex items-center gap-4">
                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                  report.status === 'ready' ? 'bg-green-100 text-green-800' :
                  report.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-purple-100 text-purple-800'
                }`}>
                  {report.status}
                </span>

                {report.status === 'ready' && (
                  <button
                    className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 transition"
                    onClick={() => downloadCSVFromReports([report], `report-${report.id}.csv`)}
                  >
                    Download
                  </button>
                )}
              </div>
            </div>
          ))}

          {filteredReports.length === 0 && (
            <div className="text-center py-8 text-gray-500">No reports match the selected filters.</div>
          )}
        </div>
      </div>

      {/* generated / fallback results */}
      {message && <div className="mb-4 text-sm text-gray-700">{message}</div>}

      {generated && (
        <div className="bg-white rounded-2xl shadow-lg p-4 mb-8">
          <div className="flex justify-between items-center mb-3">
            <div className="font-semibold">Generated Results</div>
            <div className="flex gap-2">
              <button onClick={() => downloadCSVFromReports(generated, `generated-reports-${Date.now()}.csv`)} className="px-3 py-1 bg-orange-600 text-white rounded">Download CSV</button>
            </div>
          </div>

          <div className="divide-y divide-gray-200">
            {generated.map((r, i) => (
              <div key={i} className="py-2">
                <div className="text-sm font-medium">{r.name}</div>
                <div className="text-xs text-gray-500">{r.type} • {fmt(r.date)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* backend download link (if provided) */}
      {downloadUrl && (
        <div className="mb-4">
          <a href={downloadUrl} target="_blank" rel="noreferrer" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition">
            Download Report
          </a>
        </div>
      )}
    </div>
  );
};

export default Reports;
